import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Card, ButtonGroup } from 'react-bootstrap';
import { FaStar, FaStarHalfAlt, FaRegStar, FaThumbsUp, FaThumbsDown } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { t, tf } from '../utils/i18n';

const MUSIC_GENRES = [
  'Bachata',
  'Balada',
  'Blues',
  'Bossa nova',
  'Cumbia',
  'Cumbia argentina',
  'Cumbia villera',
  'Champeta',
  'Country',
  'Cuarteto',
  'Dance',
  'Disco',
  'Electrónica',
  'Flamenco',
  'Folk',
  'Funk',
  'Gospel',
  'Guaracha',
  'Hip hop',
  'House',
  'Indie',
  'Jazz',
  'K-pop',
  'Latina',
  'Mambo',
  'Mariachi',
  'Merengue',
  'Metal',
  'Música clásica',
  'Música infantil',
  'New age',
  'Pop',
  'Punk',
  'Ranchera',
  'Rap',
  'Reggae',
  'Reggaeton',
  'Rhythm and blues',
  'Rock',
  'Rock alternativo',
  'Romántica',
  'Salsa',
  'Samba',
  'Ska',
  'Soul',
  'Tango',
  'Tecno',
  'Trap',
  'Vallenato',
  'Vals',
  'Villancicos',
  'World music',
  'Zamba',
  'Varios'
];

const RatingModal = ({ show, onHide, content, onSubmit, isEdit = false }) => {
  const { language } = useLanguage();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [ratingMethod, setRatingMethod] = useState('slider');
  const [notes, setNotes] = useState('');
  const [genre, setGenre] = useState('');

  const isMusic = content?.type === 'music';
  const showGenreEdit = isEdit && isMusic;

  const getInitialGenre = (c) => {
    if (!c || !c.genre) return '';
    if (Array.isArray(c.genre)) return c.genre[0] || '';
    const g = String(c.genre).split(',')[0].trim() || '';
    return g;
  };

  const genreOptions = MUSIC_GENRES;

  React.useEffect(() => {
    if (show && content) {
      if (isEdit) {
        setRating(content.user_rating || 0);
        setNotes(content.notes || '');
        setGenre(getInitialGenre(content));
      } else {
        setRating(0);
        setNotes('');
        setGenre('');
      }
    }
  }, [show, content, isEdit]);

  const handleSubmit = () => {
    if (rating > 0) {
      if (showGenreEdit) {
        onSubmit(rating, notes, genre.trim() || undefined);
      } else {
        onSubmit(rating, notes);
      }
    }
  };

  const handleStarClick = (value) => {
    setRating(value);
  };

  const handleStarHover = (value) => {
    setHoveredRating(value);
  };

  const handleStarLeave = () => {
    setHoveredRating(0);
  };

  const handleSliderChange = (e) => {
    setRating(parseFloat(e.target.value));
  };

  const handleButtonClick = (value) => {
    setRating(value);
  };

  const handleThumbClick = (isPositive) => {
    setRating(isPositive ? 8 : 3); // 8 para positivo, 3 para negativo
  };

  const renderStars = () => {
    const stars = [];
    const displayRating = hoveredRating || rating;

    for (let i = 1; i <= 10; i++) {
      const isFilled = i <= displayRating;
      stars.push(
        <span
          key={i}
          className="star-rating"
          onClick={() => handleStarClick(i)}
          onMouseEnter={() => handleStarHover(i)}
          onMouseLeave={handleStarLeave}
          style={{ cursor: 'pointer', marginRight: '2px' }}
        >
          {isFilled ? (
            <FaStar className="text-warning" size={20} />
          ) : (
            <FaRegStar className="text-muted" size={20} />
          )}
        </span>
      );
    }

    return stars;
  };

  const getRatingText = (rating) => {
    if (rating === 0) return t('ratingModal.selectRating', language);
    if (rating <= 2) return t('ratingModal.veryBad', language);
    if (rating <= 4) return t('ratingModal.bad', language);
    if (rating <= 6) return t('ratingModal.regular', language);
    if (rating <= 8) return t('ratingModal.good', language);
    return t('ratingModal.excellent', language);
  };

  const renderSlider = () => (
    <div className="mb-3">
      <Form.Label>
        <strong>{tf('ratingModal.ratingValue', { value: rating.toFixed(1) }, language)}</strong>
      </Form.Label>
      <Form.Range
        min="0"
        max="10"
        step="0.1"
        value={rating}
        onChange={handleSliderChange}
        className="mb-2"
      />
      <div className="d-flex justify-content-between text-muted small">
        <span>0</span>
        <span>5</span>
        <span>10</span>
      </div>
    </div>
  );

  const renderButtons = () => (
    <div className="mb-3">
      <Form.Label><strong>{t('ratingModal.rating', language)}:</strong></Form.Label>
      <div className="d-flex flex-wrap gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(value => (
          <Button
            key={value}
            variant={rating === value ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => handleButtonClick(value)}
            className="px-3"
          >
            {value}
          </Button>
        ))}
      </div>
    </div>
  );

  const renderThumbs = () => (
    <div className="mb-3">
      <Form.Label><strong>{t('ratingModal.didYouLike', language)}</strong></Form.Label>
      <div className="d-flex gap-3 justify-content-center">
        <Button
          variant={rating >= 7 ? 'success' : 'outline-success'}
          size="lg"
          onClick={() => handleThumbClick(true)}
          className="d-flex align-items-center gap-2"
        >
          <FaThumbsUp />
          {t('ratingModal.liked', language)}
        </Button>
        <Button
          variant={rating <= 4 ? 'danger' : 'outline-danger'}
          size="lg"
          onClick={() => handleThumbClick(false)}
          className="d-flex align-items-center gap-2"
        >
          <FaThumbsDown />
          {t('ratingModal.disliked', language)}
        </Button>
      </div>
    </div>
  );

  const renderRatingMethod = () => {
    switch (ratingMethod) {
      case 'stars':
        return (
          <div className="mb-3">
            <Form.Label><strong>{t('ratingModal.rating', language)}:</strong></Form.Label>
            <div className="d-flex align-items-center gap-2">
              {renderStars()}
              <span className="ms-2">{rating}/10</span>
            </div>
          </div>
        );
      case 'slider':
        return renderSlider();
      case 'buttons':
        return renderButtons();
      case 'thumbs':
        return renderThumbs();
      default:
        return renderSlider();
    }
  };

  if (!content) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? t('ratingModal.editRating', language) : t('ratingModal.rate', language)}: {content.title}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <Row>
          <Col md={4}>
            <Card>
              <Card.Body className="text-center">
                <h6>{t('ratingModal.contentInfo', language)}</h6>
                <p className="mb-1"><strong>{t('ratingModal.type', language)}:</strong> {content.type}</p>
                <p className="mb-1"><strong>{t('ratingModal.genreLabel', language)}:</strong> {content.genre}</p>
                {content.year && <p className="mb-1"><strong>{t('ratingModal.year', language)}:</strong> {content.year}</p>}
                {content.artist && <p className="mb-1"><strong>{t('ratingModal.artist', language)}:</strong> {content.artist}</p>}
                {content.director && <p className="mb-1"><strong>{t('ratingModal.director', language)}:</strong> {content.director}</p>}
                {content.type !== 'music' && content.actors && content.actors.length > 0 && (
                  <p className="mb-0">
                    <strong>{t('ratingModal.actors', language)}:</strong><br />
                    <small>{content.actors.slice(0, 3).join(', ')}</small>
                  </p>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={8}>
            <div className="rating-section">
              <h5 className="mb-3">{t('ratingModal.howWouldYouRate', language)}</h5>
              
              {/* Selector de método de calificación */}
              <div className="mb-4">
                <Form.Label><strong>{t('ratingModal.ratingMethod', language)}:</strong></Form.Label>
                <ButtonGroup className="w-100">
                  <Button
                    variant={ratingMethod === 'slider' ? 'primary' : 'outline-primary'}
                    onClick={() => setRatingMethod('slider')}
                    size="sm"
                  >
                    🎚️ {t('ratingModal.slider', language)}
                  </Button>
                  <Button
                    variant={ratingMethod === 'buttons' ? 'primary' : 'outline-primary'}
                    onClick={() => setRatingMethod('buttons')}
                    size="sm"
                  >
                    🔢 {t('ratingModal.buttons', language)}
                  </Button>
                  <Button
                    variant={ratingMethod === 'stars' ? 'primary' : 'outline-primary'}
                    onClick={() => setRatingMethod('stars')}
                    size="sm"
                  >
                    ⭐ {t('ratingModal.stars', language)}
                  </Button>
                  <Button
                    variant={ratingMethod === 'thumbs' ? 'primary' : 'outline-primary'}
                    onClick={() => setRatingMethod('thumbs')}
                    size="sm"
                  >
                    👍👎 {t('ratingModal.thumbs', language)}
                  </Button>
                </ButtonGroup>
              </div>

              {/* Método de calificación seleccionado */}
              {renderRatingMethod()}

              {/* Descripción de la calificación */}
              {rating > 0 && (
                <div className="alert alert-info">
                  <strong>{tf('ratingModal.ratingValue', { value: rating.toFixed(1) }, language)}</strong> - {getRatingText(rating)}
                </div>
              )}

              {/* Género editable solo para música en modo edición */}
              {showGenreEdit && (() => {
                const initialGenre = getInitialGenre(content);
                const options = [
                  { value: '', label: t('ratingModal.selectGenrePlaceholder', language) },
                  ...genreOptions.map(g => ({ value: g, label: g }))
                ];
                if (initialGenre && !genreOptions.includes(initialGenre)) {
                  options.push({ value: initialGenre, label: initialGenre });
                }
                return (
                  <div className="mb-3">
                    <Form.Label><strong>{t('ratingModal.genre', language)}:</strong></Form.Label>
                    <Form.Select
                      value={genre}
                      onChange={(e) => setGenre(e.target.value)}
                      aria-label={t('ratingModal.selectGenreAria', language)}
                    >
                      {options.map(opt => (
                        <option key={opt.value || 'empty'} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">{t('ratingModal.selectCorrectGenre', language)}</Form.Text>
                  </div>
                );
              })()}

              {/* Campo de notas */}
              <div className="mb-3">
                <Form.Label><strong>{t('ratingModal.notes', language)}:</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={t('ratingModal.notesPlaceholder', language)}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              {content.description && (
                <div className="mt-3">
                  <h6>{t('ratingModal.description', language)}:</h6>
                  <p className="text-muted small">{content.description}</p>
                </div>
              )}

              {content.reason && (
                <div className="mt-3">
                  <h6>{t('ratingModal.whyRecommended', language)}</h6>
                  <p className="text-primary small">{content.reason}</p>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('ratingModal.cancel', language)}
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={rating === 0}
        >
          {isEdit ? t('ratingModal.save', language) : t('ratingModal.markAsWatched', language)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RatingModal;
